// API types and pure formatting/model helpers for ObservabilityTab.
export type StepKind =
  | "actor_wake"
  | "consolidation_only_wake"
  | "external_compact_seed";

export type SourceKind =
  | "static_system"
  | "tool_definitions"
  | "phase_prompt"
  | "runtime_inject_semantic"
  | "runtime_inject_episodic"
  | "runtime_inject_growth_nudge"
  | "runtime_inject_void_text_nudge"
  | "runtime_inject_delivered_tool_checkpoint"
  | "resumed_history"
  | "mailbox_drain"
  | "wake_header"
  | "compact_seed"
  | "compact_tail"
  | "assistant_output"
  | "tool_result";

export type MetaAccent = "emerald" | "amber" | "rose";
export type ContextTransport = "local_full_prompt" | "server_state_replay";

export interface ReplayContextFields {
  contextTransport: ContextTransport | null;
  contextReplayProvider: string | null;
  contextReplayIdentityHash: string | null;
  replayedPrefixMessageCount: number | null;
  localDeltaInputMessageCount: number | null;
  durableDeltaMessageCount: number | null;
}

export interface ReplayContextSummary {
  contextReplayProvider: string | null;
  contextReplayIdentityHash: string | null;
  replayedPrefixMessageCount: number;
  localDeltaInputMessageCount: number;
  durableDeltaMessageCount: number;
  effectiveMessageCount: number;
}

export interface WakeSummary {
  wakeGroupId: string;
  actorInstanceId: string | null;
  stepKind: StepKind;
  phase: string | null;
  triggerReason: string | null;
  startedAt: string;
  totalDurationMs: number;
  totalInputTokens: number;
  totalOutputTokens: number;
  totalCacheReadTokens: number;
  totalCacheWriteTokens: number;
  /** Milliseconds the wake blocked on the per-channel mutex before the first
   *  generateText started. null when unmeasured (pre-migration / no-lock wakes). */
  lockWaitMs: number | null;
  stepCount: number;
  finishReason: string | null;
  /** Error kind of the first errored step (e.g. `rate_limit`, `timeout`), or
   *  null when the wake didn't error. Drives the row's error badge. */
  errorKind: string | null;
  providerStatusCode: number | null;
  providerErrorCode: string | null;
  providerErrorBody: string | null;
  providerErrorType: string | null;
  providerRetryAfter: string | null;
  prefixSha: string | null;
  systemPromptSha: string | null;
  toolsSha: string | null;
  modelRef: string;
  usedReplay: boolean;
  replayProvider: string | null;
  replayedPrefixMessageCount: number | null;
  localDeltaInputMessageCount: number | null;
  durableDeltaMessageCount: number | null;
}

export interface StepEntity extends ReplayContextFields {
  id: string;
  wakeGroupId: string;
  stepIndexInWake: number;
  stepKind: StepKind;
  phase: string | null;
  startedAt: string;
  durationMs: number | null;
  lockWaitMs: number | null;
  inputTokens: number | null;
  outputTokens: number | null;
  cacheReadTokens: number | null;
  cacheWriteTokens: number | null;
  noCacheTokens: number | null;
  finishReason: string | null;
  modelRef: string;
  temperature: number | null;
  maxOutputTokens: number | null;
  toolsSha: string | null;
  systemPromptSha: string | null;
  prefixSha: string | null;
  cacheMarkerValid: boolean | null;
  cacheMarkerReason: string | null;
  errorKind: string | null;
  errorText: string | null;
  providerStatusCode: number | null;
  providerErrorCode: string | null;
  providerErrorBody: string | null;
  providerErrorType: string | null;
  providerRetryAfter: string | null;
  voidTextNudgeInjected: boolean;
  voidTextNudgeIndex: number | null;
  waitIgnoredDueToVoidText: boolean;
  ignoredTerminalToolNamesJson: string | null;
  openrouterGenerationId: string | null;
  openrouterStatsJson: string | null;
  triggerReason: string | null;
  triggerChannelMessageId: string | null;
  /** Per-step assistant-output classification (tool-call vs void text) for the
   *  step-list indicator. Optional for resilience against older payloads. */
  outputSummary?: {
    kind: "delivered_tool" | "void_text" | "tool_work" | "empty";
    toolNames: string[];
    hasText: boolean;
    textPreview: string | null;
  };
}

export const formatCacheMarkerValid = (
  valid: StepEntity["cacheMarkerValid"],
) => {
  if (valid === null) {
    return "—";
  }
  if (valid) {
    return "yes";
  }
  return "no";
};

export function getReplayContextSummary(
  step: ReplayContextFields,
): ReplayContextSummary | null {
  if (
    step.contextTransport !== "server_state_replay" ||
    step.replayedPrefixMessageCount === null ||
    step.localDeltaInputMessageCount === null ||
    step.durableDeltaMessageCount === null
  ) {
    return null;
  }
  return {
    contextReplayIdentityHash: step.contextReplayIdentityHash,
    contextReplayProvider: step.contextReplayProvider,
    durableDeltaMessageCount: step.durableDeltaMessageCount,
    effectiveMessageCount:
      step.replayedPrefixMessageCount + step.durableDeltaMessageCount,
    localDeltaInputMessageCount: step.localDeltaInputMessageCount,
    replayedPrefixMessageCount: step.replayedPrefixMessageCount,
  };
}

export const getMetaAccentClass = (accent: MetaAccent | undefined) => {
  if (accent === "emerald") {
    return "text-emerald-600 dark:text-emerald-400";
  }
  if (accent === "amber") {
    return "text-amber-600 dark:text-amber-400";
  }
  if (accent === "rose") {
    return "text-rose-600 dark:text-rose-400";
  }
  return "text-slate-700 dark:text-slate-200";
};

export interface MessageEntity {
  id: string;
  stepId: string;
  sequence: number;
  role: "system" | "user" | "assistant" | "tool";
  sourceKind: SourceKind;
  agentModeMessageId: string | null;
  textSha: string | null;
  cacheControl: string | null;
  blocks: ContentBlockEntity[];
}

export interface ContentBlockEntity {
  id: string;
  messageId: string;
  sequence: number;
  type: string;
  textSha: string | null;
  toolCallId: string | null;
  toolName: string | null;
  argsSha: string | null;
  resultSha: string | null;
}

export interface StepDetail {
  step: StepEntity;
  messages: MessageEntity[];
}

export interface SnippetEntity {
  sha: string;
  content: string;
  byteLength: number;
  tokenEstimate: number | null;
  firstSeenAt: string;
}

export type GenerationStatsResponse =
  | { cached: boolean; stats: object; status: "ready" }
  | { message: string; status: "expired" };

export type GenerationStatsState =
  | { status: "idle" }
  | { status: "loading" }
  | { message: string; status: "error" }
  | { message: string; status: "expired" }
  | { cached: boolean; stats: object; status: "ready" };

// ---------------------------------------------------------------------------
// Anomaly detection — mirrors server-side logic for client-side color-coding.
// Runs against the series fetched by the list endpoint.
export type AnomalyFlag =
  | "cache_cold"
  | "cache_miss"
  | "cache_hit_drop"
  | "cache_boundary_moved"
  | "error"
  | "long"
  | "multi_step";

// Threshold for classifying a zero-read wake as expected cold vs unexpected miss.
// Under 1h TTL, a wake ≥55min after the previous one is naturally past cache
// Life — treat as cold. Shorter gaps with zero reads imply the cache should have
// Been alive: prefix drift, model rollover, or a real bug.
const COLD_THRESHOLD_MS = 55 * 60 * 1000;

export function flagsFor(
  current: WakeSummary,
  prior: WakeSummary | undefined,
): AnomalyFlag[] {
  const flags: AnomalyFlag[] = [];
  const noCache = Math.max(
    current.totalInputTokens -
      current.totalCacheReadTokens -
      current.totalCacheWriteTokens,
    0,
  );
  const hitDenom = current.totalCacheReadTokens + noCache;
  const hitPct = hitDenom > 0 ? current.totalCacheReadTokens / hitDenom : null;

  if (current.totalCacheReadTokens === 0 && current.totalInputTokens > 0) {
    const priorTs = prior ? Date.parse(prior.startedAt) : Number.NaN;
    const currentTs = Date.parse(current.startedAt);
    const elapsedMs =
      Number.isFinite(priorTs) && Number.isFinite(currentTs)
        ? currentTs - priorTs
        : Number.POSITIVE_INFINITY;
    flags.push(elapsedMs >= COLD_THRESHOLD_MS ? "cache_cold" : "cache_miss");
  }
  if (prior && hitPct !== null) {
    const priorNoCache = Math.max(
      prior.totalInputTokens -
        prior.totalCacheReadTokens -
        prior.totalCacheWriteTokens,
      0,
    );
    const priorDenom = prior.totalCacheReadTokens + priorNoCache;
    if (priorDenom > 0) {
      const priorHit = prior.totalCacheReadTokens / priorDenom;
      if (priorHit - hitPct >= 0.2) {
        flags.push("cache_hit_drop");
      }
    }
  }
  if (
    prior &&
    current.prefixSha &&
    prior.prefixSha &&
    current.prefixSha !== prior.prefixSha &&
    current.systemPromptSha === prior.systemPromptSha &&
    current.toolsSha === prior.toolsSha
  ) {
    flags.push("cache_boundary_moved");
  }
  if (current.finishReason === "error" || current.errorKind !== null) {
    flags.push("error");
  }
  if (current.totalDurationMs > 30_000) {
    flags.push("long");
  }
  if (current.stepCount > 1) {
    flags.push("multi_step");
  }
  return flags;
}

export function cacheHitPct(w: WakeSummary): number | null {
  const noCache = Math.max(
    w.totalInputTokens - w.totalCacheReadTokens - w.totalCacheWriteTokens,
    0,
  );
  const denom = w.totalCacheReadTokens + noCache;
  return denom > 0 ? w.totalCacheReadTokens / denom : null;
}

// ---------------------------------------------------------------------------
// Formatting helpers — tight, monospace-friendly.
// ---------------------------------------------------------------------------

export function fmtTokens(tokenCount: number | null | undefined): string {
  if (tokenCount === null || tokenCount === undefined) {
    return "—";
  }
  if (tokenCount >= 10_000) {
    return `${Math.round(tokenCount / 1000)}k`;
  }
  if (tokenCount >= 1000) {
    return `${(tokenCount / 1000).toFixed(1)}k`;
  }
  return String(tokenCount);
}

export function fmtDuration(ms: number | null): string {
  if (ms === null) {
    return "—";
  }
  if (ms < 1000) {
    return `${ms}ms`;
  }
  return `${(ms / 1000).toFixed(1)}s`;
}

/**
 * Wall-clock time a wake/step appeared to take from the user's perspective.
 * Equals lock-wait + generateText time because capture's startMs is stamped
 * AFTER acquire() resolves (see llmCallCapture.ts), so the two never overlap.
 * Returns null when any side is missing so callers can decide fallback.
 */
export function totalWallClockMs(
  durationMs: number | null,
  lockWaitMs: number | null,
): number | null {
  if (durationMs === null) {
    return null;
  }
  return durationMs + (lockWaitMs ?? 0);
}

/**
 * Threshold below which lock-wait is visually noise — rendering "0ms lock"
 * or "3ms lock" on every uncontended wake clutters the row without adding
 * information. Above this we surface the split explicitly.
 */
export const LOCK_WAIT_VISIBLE_MS = 50;

export function fmtTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString("en-US", {
    hour: "2-digit",
    hour12: false,
    minute: "2-digit",
    second: "2-digit",
  });
}

export function fmtHitPct(pct: number | null): string {
  if (pct === null) {
    return "—";
  }
  return `${Math.round(pct * 100)}%`;
}

/**
 * Color the cache-hit cell by the meaningfulness of any drop vs prior wake.
 * - rose: meaningful drop (≥15pp) OR absolute floor below 50%
 * - amber: notable drop (5–15pp) OR steady-but-mid hit (50–75%)
 * - emerald: stable / improving / high steady hit (≥75%)
 * - slate: no data
 */
export function hitPctColor(
  current: number | null,
  prior: number | null | undefined,
): string {
  if (current === null) {
    return "text-slate-500";
  }
  if (current < 0.5) {
    return "text-rose-700 dark:text-rose-300";
  }
  if (prior !== null && prior !== undefined) {
    const drop = prior - current;
    if (drop >= 0.15) {
      return "text-rose-700 dark:text-rose-300";
    }
    if (drop >= 0.05) {
      return "text-amber-700 dark:text-amber-300";
    }
  }
  if (current < 0.75) {
    return "text-amber-700 dark:text-amber-300";
  }
  return "text-emerald-700 dark:text-emerald-300";
}

export function hitPctTooltip(
  current: number | null,
  prior: number | null | undefined,
): string {
  if (current === null) {
    return "Cache hit pct unavailable.";
  }
  const cur = `${Math.round(current * 100)}%`;
  if (prior === null || prior === undefined) {
    return `Cache hit ${cur}. No prior wake to compare.`;
  }
  const deltaPp = Math.round((current - prior) * 100);
  const sign = deltaPp > 0 ? "+" : "";
  return `Cache hit ${cur} (${sign}${deltaPp}pp vs prior ${Math.round(prior * 100)}%).`;
}

export function shortSha(sha: string | null, len = 6): string {
  return sha ? sha.slice(0, len) : "—";
}

/**
 * Bedrock inference profile ARNs are long — trim to the last segment after
 * "global." or the inference-profile id for display. Leaves short refs like
 * "bedrock/..." untouched.
 */
export function modelRefTail(modelRef: string): string {
  if (modelRef.length <= 40) {
    return modelRef;
  }
  const globalIdx = modelRef.indexOf("global.");
  if (globalIdx !== -1) {
    return modelRef.slice(globalIdx);
  }
  const profileIdx = modelRef.indexOf("inference-profile/");
  if (profileIdx !== -1) {
    return `…/${modelRef.slice(profileIdx + 18)}`;
  }
  return `…${modelRef.slice(-40)}`;
}

// ---------------------------------------------------------------------------
// Window selector — matches CostTab vocabulary exactly.
// ---------------------------------------------------------------------------

export function computeStepHitPct(step: StepEntity): number | null {
  const input = step.inputTokens ?? 0;
  const cr = step.cacheReadTokens ?? 0;
  const nc =
    step.noCacheTokens ??
    Math.max(input - cr - (step.cacheWriteTokens ?? 0), 0);
  const denom = cr + nc;
  return denom > 0 ? cr / denom : null;
}

// ---------------------------------------------------------------------------
// Anomaly badge — color-coded pill.
// ---------------------------------------------------------------------------

// Keep labels ≤5 chars — they render in a pill at 9px uppercase, and narrow
export function formatWakeReplayBadge(wake: WakeSummary): string {
  if (
    wake.replayedPrefixMessageCount === null ||
    wake.durableDeltaMessageCount === null
  ) {
    return "replay";
  }
  return `replay ${wake.replayedPrefixMessageCount}+${wake.durableDeltaMessageCount}`;
}

export function formatWakeReplayTitle(wake: WakeSummary): string {
  const provider = wake.replayProvider ?? "server-state replay";
  if (
    wake.replayedPrefixMessageCount === null ||
    wake.localDeltaInputMessageCount === null ||
    wake.durableDeltaMessageCount === null
  ) {
    return `Wake used ${provider}.`;
  }
  return `${provider}: ${wake.replayedPrefixMessageCount} durable messages replayed server-side, ${wake.localDeltaInputMessageCount} durable input messages composed locally, ${wake.durableDeltaMessageCount} durable messages added by this step.`;
}

export interface ProviderErrorFields {
  errorKind: string | null;
  errorText?: string | null;
  providerStatusCode: number | null;
  providerErrorCode: string | null;
  providerErrorBody: string | null;
  providerErrorType: string | null;
  providerRetryAfter: string | null;
}

export function formatStepErrorLabel(step: ProviderErrorFields): string {
  const providerLabel = formatProviderErrorLabel(step);
  const legacyTimeout =
    step.errorKind === "abort" &&
    /TimeoutError|aborted due to timeout|actor_(?:provider_step|wake)_timeout/iu.test(
      step.errorText ?? "",
    );
  const base = legacyTimeout ? "timeout" : (step.errorKind ?? "error");
  return providerLabel ? `${base} · ${providerLabel}` : base;
}

function formatProviderErrorLabel(step: ProviderErrorFields): string | null {
  const specific = formatProviderSpecificError(step);
  const retryAfter = step.providerRetryAfter
    ? `retry-after ${step.providerRetryAfter}`
    : null;
  const parts = [
    step.providerStatusCode !== null ? `HTTP ${step.providerStatusCode}` : null,
    specific ? `(${specific})` : null,
    retryAfter,
  ].filter((part): part is string => part !== null);
  return parts.length > 0 ? parts.join(" ") : null;
}

function formatProviderSpecificError(step: ProviderErrorFields): string | null {
  const code = step.providerErrorCode;
  const type = step.providerErrorType;
  if (code && type && code !== type) {
    return `${code} / ${type}`;
  }
  return code ?? type;
}
